/**
 * The editor sync status as a pure reducer. The sync context sends it what
 * happened and runs the effects it answers with.
 *
 * @module platform/editor-sync-reducer
 */

import type { EditLease } from "@/server/types";

export type EditorSyncStatus =
  | "idle"
  | "loading"
  | "read-only"
  | "saved-locally"
  | "saving-backend"
  | "saved-backend"
  | "offline"
  | "reconnecting"
  | "locked"
  | "conflict"
  | "unauthorized"
  | "error";

/**
 * Refusals that stand until an outcome resolves them: a save landing, a retry,
 * a re-read, the lease coming back, or closing. An edit or a connection change
 * still sends, and the refusal stays on screen while that save is out.
 */
const TERMINAL_STATUSES = new Set<EditorSyncStatus>([
  "locked",
  "conflict",
  "unauthorized",
  "error",
]);

/** Re-reads in a row a revision conflict may take before it is left to the reader. */
export const CONFLICT_REBASE_LIMIT = 3;

/** How many times a re-read is asked while saves keep overtaking it. */
export const OVERTAKEN_REREADS = 3;

export function hasUnsavedEditorChanges(status: EditorSyncStatus): boolean {
  return (
    status === "saved-locally" ||
    status === "saving-backend" ||
    status === "offline" ||
    status === "reconnecting" ||
    status === "conflict" ||
    status === "error"
  );
}

export interface EditorSyncState {
  readonly status: EditorSyncStatus;
  readonly error: Error | null;
  /** The programme lease this client holds; the context renews and releases it. */
  readonly lease: EditLease | null;
  /** Whether structural editing is open here: a lease, or a change case needing none. */
  readonly holdsStructuralLease: boolean;
  readonly saveInFlight: boolean;
  /** A send asked for while one is in flight, owed once it settles. */
  readonly savePending: boolean;
  /**
   * Work on screen no save has landed. Carried on the events, since only the
   * context sees the buffer, and read where a refusal hides it from the status.
   */
  readonly unsent: boolean;
  readonly online: boolean;
  readonly conflictRebases: number;
  readonly overtakenRereads: number;
  /** A lapsed-lease refusal already asked for the lease again, with no grant or landed save since. */
  readonly leaseReasked: boolean;
  /**
   * Why the last background re-read failed, so what is on screen may be
   * behind storage. Apart from the status: nothing typed is unsaved for it.
   */
  readonly behind: Error | null;
  /**
   * A save was out when that read failed. Its answer predates what the read
   * would have brought, so its landing does not mean the copy caught up.
   */
  readonly behindPastSave: boolean;
}

export type EditorSyncEvent =
  /** An edit left the session's window. `unsent` when a record is open and differs from its baseline. */
  | { type: "edited"; online: boolean; unsent: boolean }
  | { type: "online"; unsent: boolean }
  | { type: "offline"; unsent: boolean }
  /** A send asked for while one is in flight. */
  | { type: "save-deferred" }
  | { type: "save-started" }
  /** `settled` when the buffer matches what the save left as the baseline. */
  | { type: "save-succeeded"; settled: boolean }
  /**
   * `racedLeaseRequest`: this client asked for a lease while it was out, so
   * the lease that refused it may be gone or now this client's.
   */
  | { type: "save-failed"; error: Error; racedLeaseRequest?: boolean }
  /** Settled after the editor closed or opened another programme. */
  | { type: "save-abandoned" }
  /** Null when leases are off, which still opens structural editing. */
  | { type: "lease-acquired"; lease: EditLease | null }
  | { type: "lease-waived" }
  | { type: "lease-refused"; error: Error }
  | { type: "lease-renewed"; leaseId: string; lease: EditLease }
  | { type: "lease-lost"; leaseId: string; error: Error }
  | { type: "lease-released" }
  | { type: "read-started"; rereading: boolean; discardLocal: boolean }
  /**
   * `conflict` when a re-read after a refused save found both sides changed
   * one field. `keptUnsent` when work typed during the read was kept unsent.
   */
  | { type: "read-landed"; readOnly: boolean; conflict: Error | null; keptUnsent: boolean }
  | { type: "read-overtaken" }
  /** `restore` is the status to keep when the read failed over saved work. */
  | { type: "read-failed"; error: Error; conflict: Error | null; restore: EditorSyncStatus | null }
  | { type: "reload-requested" }
  | { type: "retried"; online: boolean }
  | { type: "closed" };

export type EditorSyncEffect =
  | { type: "schedule-save" }
  /** Send again a save refused for a lease while this client asked for one, or that lapsed. */
  | { type: "resend" }
  /** Ask again for a lease this client lost, once online. Before any resend, which waits on it. */
  | { type: "ask-lease" }
  /** Re-read under the typed work and resend it. */
  | { type: "rebase"; conflict: Error };

export interface EditorSyncTransition {
  readonly state: EditorSyncState;
  readonly effects: readonly EditorSyncEffect[];
}

export function initialEditorSyncState(online: boolean): EditorSyncState {
  return {
    status: "idle",
    error: null,
    lease: null,
    holdsStructuralLease: false,
    saveInFlight: false,
    savePending: false,
    unsent: false,
    online,
    conflictRebases: 0,
    overtakenRereads: 0,
    leaseReasked: false,
    behind: null,
    behindPastSave: false,
  };
}

/**
 * Whether work on screen has not reached the platform, including work typed
 * under a refusal whose status does not say so.
 */
export function holdsUnsavedWork(state: EditorSyncState): boolean {
  return hasUnsavedEditorChanges(state.status) || state.unsent;
}

function apiStatus(error: Error): unknown {
  return "status" in error ? (error as Error & { status?: unknown }).status : undefined;
}

/** A save refused only because the stored revision moved, which a re-read can answer. */
export function isRevisionConflict(error: Error): boolean {
  return /revision conflict/i.test(error.message);
}

function isAuthorizationError(error: Error): boolean {
  const status = apiStatus(error);
  return status === 401 || status === 403 || /not authorized|unauthori[sz]ed/i.test(error.message);
}

/**
 * A lease held by another editor. The API reports it as a state conflict,
 * told apart from the others only by its message: who holds it, or, when a
 * race's winner has already gone, that another editor took it first.
 */
export function isLeaseConflict(error: Error): boolean {
  return /currently being edited by|took this lease first/i.test(error.message);
}

/**
 * This client's own lease ended, or a save went without the one this user
 * holds: asking for it again answers either, whoever the reader is.
 */
export function isLeaseLapsed(error: Error): boolean {
  return /lease is missing or has expired|your own editing lease/i.test(error.message);
}

/** A failure that says nothing of the request: the connection, a throttle, or a server fault. */
export function isTransientFailure(error: Error): boolean {
  const status = apiStatus(error);
  return typeof status !== "number" || status >= 500 || status === 408 || status === 429;
}

/** Any 409, the refusal of the stored state: another editor's lease, a moved revision, or no edits taken. */
export function isStateConflict(error: Error): boolean {
  return apiStatus(error) === 409;
}

/** A refusal from a save or a read. Another editor's lease is a 409 too, but not a conflict. */
function classifyRefusal(error: Error): EditorSyncStatus {
  if (isLeaseConflict(error)) {
    return "locked";
  }
  // A lapse asking again has not answered: nobody else is editing.
  if (isLeaseLapsed(error)) {
    return "error";
  }
  const status = apiStatus(error);
  if (status === 409 || isRevisionConflict(error)) {
    return "conflict";
  }
  if (isAuthorizationError(error)) {
    return "unauthorized";
  }
  return "error";
}

/**
 * Only a refused lease means somebody else holds it; a timeout or a 500
 * reported as locked tells the lead a colleague is editing.
 */
function leaseRefusalStatus(error: Error): EditorSyncStatus {
  if (isAuthorizationError(error)) {
    return "unauthorized";
  }
  // Every state conflict is a 409, a programme past drafting included.
  return isLeaseConflict(error) ? "locked" : "error";
}

/** One instance, so an edit made offline leaves the error, and the render, as it was. */
const OFFLINE_ERROR = new Error(
  "The device is offline. Your changes are still open in this browser.",
);

const SCHEDULE_SAVE: readonly EditorSyncEffect[] = [{ type: "schedule-save" }];
const RESEND: readonly EditorSyncEffect[] = [{ type: "resend" }];
const ASK_LEASE: readonly EditorSyncEffect[] = [{ type: "ask-lease" }];

function to(
  state: EditorSyncState,
  effects: readonly EditorSyncEffect[] = [],
): EditorSyncTransition {
  return { state, effects };
}

/**
 * A lock or conflict is settled by reloading. Replaced by a failure it would
 * offer Retry, which sends the copy a Reload was asked to throw away; by an
 * authorization refusal, no button at all.
 */
function standsOver(state: EditorSyncState, status: EditorSyncStatus): boolean {
  return (
    (state.status === "locked" || state.status === "conflict") &&
    (status === "error" || status === "unauthorized")
  );
}

/** Send what is owed once the save in flight has settled. */
function settle(state: EditorSyncState, owed: boolean): EditorSyncTransition {
  const next = { ...state, saveInFlight: false, savePending: false };
  return owed ? to(next, SCHEDULE_SAVE) : to(next);
}

/** Statuses saying work waits to send, which nothing is once the buffer matches its baseline. */
const WAITING_STATUSES = new Set<EditorSyncStatus>(["saved-locally", "reconnecting", "offline"]);

/** Nothing unsent: a waiting status is saved, unless a save in flight will say so itself. */
function settleUnsent(state: EditorSyncState): EditorSyncTransition {
  return !state.saveInFlight && WAITING_STATUSES.has(state.status)
    ? to({ ...state, status: "saved-backend", error: null })
    : to(state);
}

function reduce(state: EditorSyncState, event: EditorSyncEvent): EditorSyncTransition {
  const terminal = TERMINAL_STATUSES.has(state.status);
  switch (event.type) {
    case "edited": {
      const next = { ...state, online: event.online, unsent: event.unsent };
      if (!event.unsent) {
        return settleUnsent(next);
      }
      if (!event.online) {
        return terminal ? to(next) : to({ ...next, status: "offline", error: OFFLINE_ERROR });
      }
      return terminal
        ? to(next, SCHEDULE_SAVE)
        : to({ ...next, status: "saved-locally", error: null }, SCHEDULE_SAVE);
    }
    case "online": {
      // A save out across the outage may fail for it, and nothing else sends its
      // work again: a send is owed once it settles, which finds nothing if it lands.
      const next = {
        ...state,
        online: true,
        unsent: event.unsent,
        savePending: state.savePending || (event.unsent && state.saveInFlight),
      };
      if (!event.unsent) {
        return settleUnsent(next);
      }
      return terminal
        ? to(next, SCHEDULE_SAVE)
        : to({ ...next, status: "reconnecting", error: null }, SCHEDULE_SAVE);
    }
    case "offline": {
      const next = { ...state, online: false, unsent: event.unsent };
      if (!event.unsent) {
        return settleUnsent(next);
      }
      if (terminal) {
        return to(next);
      }
      return to({ ...next, status: "offline", error: OFFLINE_ERROR });
    }
    case "save-deferred":
      return to({ ...state, savePending: true });
    case "save-started":
      return to({
        ...state,
        saveInFlight: true,
        savePending: false,
        ...(terminal ? {} : { status: "saving-backend" as const, error: null }),
      });
    case "save-succeeded":
      return settle(
        {
          ...state,
          conflictRebases: 0,
          leaseReasked: false,
          status: event.settled ? "saved-backend" : "saved-locally",
          error: null,
          unsent: !event.settled,
          // Storage took it at the revision this copy holds, so the copy is not
          // behind, unless the save was out before the failed read.
          behind: state.behindPastSave ? state.behind : null,
          behindPastSave: false,
        },
        !event.settled || state.savePending,
      );
    case "save-failed": {
      if (isRevisionConflict(event.error) && state.conflictRebases < CONFLICT_REBASE_LIMIT) {
        // Still saving: the re-read resends what it keeps, and a save queued
        // now would carry the stale revision again.
        return to(
          {
            ...state,
            saveInFlight: false,
            savePending: false,
            conflictRebases: state.conflictRebases + 1,
            ...(terminal ? {} : { status: "saving-backend" as const, error: null }),
          },
          [{ type: "rebase", conflict: event.error }],
        );
      }
      // Sent again once no lease request is out, under whatever lease is held
      // then; another editor's refusal of that is final unless a request
      // crossed it too. With the work typed away there is nothing to send. A
      // lapsed lease goes the same way, asked for first unless a request has,
      // and only once: the same user's other tab holding it answers no ask.
      const lapsed =
        isLeaseLapsed(event.error) && !(state.leaseReasked && !event.racedLeaseRequest);
      if (lapsed || (event.racedLeaseRequest && isLeaseConflict(event.error))) {
        const asking = lapsed && !event.racedLeaseRequest;
        const next = {
          ...state,
          saveInFlight: false,
          savePending: false,
          ...(state.status === "saving-backend" ? { status: "saved-locally" as const } : {}),
          ...(asking ? { lease: null, holdsStructuralLease: false, leaseReasked: true } : {}),
        };
        return to(
          state.unsent ? next : settleUnsent(next).state,
          asking ? [...ASK_LEASE, ...RESEND] : RESEND,
        );
      }
      // A send asked for meanwhile still goes, under the refusal now shown. As
      // for a lease request or a read, a failure leaves a conflict standing.
      const status = classifyRefusal(event.error);
      return settle(
        state.status === "conflict" && status === "error"
          ? state
          : { ...state, status, error: event.error },
        state.savePending,
      );
    }
    case "save-abandoned":
      return settle(state, state.savePending);
    case "lease-acquired": {
      // The re-ask bound stands: a save refused again under this grant asks no more.
      const next = { ...state, lease: event.lease, holdsStructuralLease: true };
      // A grant answers a lock, a lapse asking again had not, and a failed
      // lease request with nothing unsent. Any other refused save leaves its
      // work unsent, and its refusal stands.
      const answered =
        state.status === "locked" ||
        (state.status === "error" && state.error !== null && isLeaseLapsed(state.error)) ||
        (!state.unsent && (state.status === "error" || state.status === "unauthorized"));
      if (!answered) {
        return to(next);
      }
      // Work typed under the lock is sendable again.
      return state.unsent
        ? to({ ...next, status: "saved-locally", error: null }, SCHEDULE_SAVE)
        : to({ ...next, status: "saved-backend", error: null });
    }
    case "lease-waived":
      return to({ ...state, holdsStructuralLease: true });
    case "lease-refused": {
      // The connection dropping under the request says only what offline says.
      if (!state.online && isTransientFailure(event.error)) {
        return to({ ...state, holdsStructuralLease: false });
      }
      const status = leaseRefusalStatus(event.error);
      return standsOver(state, status)
        ? to({ ...state, holdsStructuralLease: false })
        : to({ ...state, holdsStructuralLease: false, status, error: event.error });
    }
    case "lease-renewed":
      // A renewal landing after the lease was let go must not take it back.
      return state.lease?.id === event.leaseId ? to({ ...state, lease: event.lease }) : to(state);
    case "lease-lost": {
      // Nor report a lease this client already let go as lost. A renewal that
      // failed in transit lost nothing: dropped, the next save meets this
      // client's own lease, and the next renewal says if it has gone.
      if (state.lease?.id !== event.leaseId || isTransientFailure(event.error)) {
        return to(state);
      }
      const next = { ...state, lease: null, holdsStructuralLease: false };
      // Only another editor's lease says anybody else is editing. Anything else
      // is answered by asking again, whose refusal reports whatever it finds.
      return isLeaseConflict(event.error)
        ? to({ ...next, status: "locked", error: event.error })
        : to(next, ASK_LEASE);
    }
    case "lease-released":
      return to({ ...state, lease: null, holdsStructuralLease: false });
    case "read-started": {
      const opening = !event.rereading;
      return to({
        ...state,
        // A re-read leaves a refusal on screen, and its message with it, until
        // the read's outcome moves it. So too the offline message.
        error: opening || !(terminal || state.status === "offline") ? null : state.error,
        ...(opening ? { status: "loading" as const, savePending: false, unsent: false } : {}),
        // The retries were spent on work this read opens or throws away.
        ...(opening || event.discardLocal ? { conflictRebases: 0, leaseReasked: false } : {}),
      });
    }
    case "read-landed": {
      const next = { ...state, unsent: event.keptUnsent, behind: null, behindPastSave: false };
      if (event.readOnly) {
        return to({ ...next, status: "read-only", error: null });
      }
      if (event.conflict) {
        return to({ ...next, status: "conflict", error: event.conflict });
      }
      if (!event.keptUnsent) {
        return to({ ...next, status: "saved-backend", error: null });
      }
      // Work kept over the read is still unsent. A status saying so stands, and
      // a lease or authorization refusal stays with the work it refused.
      const keep =
        hasUnsavedEditorChanges(state.status) ||
        state.status === "locked" ||
        state.status === "unauthorized";
      return to(keep ? next : { ...next, status: "saved-locally", error: null });
    }
    case "read-overtaken":
      return to({ ...state, overtakenRereads: state.overtakenRereads + 1 });
    case "read-failed": {
      // A refusal standing now landed during the read or was there at its start,
      // and one a grant cleared meanwhile is not put back.
      if (event.restore) {
        const behind = { ...state, behind: event.error, behindPastSave: state.saveInFlight };
        return terminal || TERMINAL_STATUSES.has(event.restore)
          ? to(behind)
          : to({ ...behind, status: event.restore });
      }
      // A read has no connection check of its own: failing in transit offline
      // says only what offline says. A rebase read went out over the offline
      // status unsent work had; with none, a conflict it found stands, else the status.
      if (!state.online && state.status !== "loading" && isTransientFailure(event.error)) {
        if (state.unsent && !terminal && state.status !== "offline") {
          return to({ ...state, status: "offline", error: OFFLINE_ERROR });
        }
        if (state.unsent || terminal || !event.conflict) {
          return to(state);
        }
      }
      if (event.conflict) {
        // Another editor's lease outranks a conflict.
        return state.status === "locked"
          ? to(state)
          : to({ ...state, status: "conflict", error: event.conflict });
      }
      // A read refused for authorization never lands as read-only, so it says so.
      const status = classifyRefusal(event.error);
      return standsOver(state, status) && status !== "unauthorized"
        ? to(state)
        : to({ ...state, status, error: event.error });
    }
    case "reload-requested":
      return to({ ...state, overtakenRereads: 0 });
    case "retried":
      return to({
        ...state,
        online: event.online,
        // Retry's own send finds a save out carrying the same work and defers
        // nothing, so a send already owed once that save settles stays owed.
        savePending: state.savePending && state.saveInFlight,
        status: event.online ? "reconnecting" : "offline",
        error: null,
      });
    case "closed":
      return to({
        ...state,
        status: "idle",
        error: null,
        lease: null,
        holdsStructuralLease: false,
        savePending: false,
        unsent: false,
        conflictRebases: 0,
        overtakenRereads: 0,
        leaseReasked: false,
        behind: null,
        behindPastSave: false,
      });
    default: {
      // Unreachable: every event has a case above, and this assignment is what
      // makes tsc say so if one is ever added without one.
      const unhandled: never = event;
      throw new Error(`Unhandled editor sync event: ${JSON.stringify(unhandled)}`);
    }
  }
}

export function reduceEditorSync(
  state: EditorSyncState,
  event: EditorSyncEvent,
): EditorSyncTransition {
  // The save that was out when a read failed has answered however it ended,
  // so the next one to land is a save sent since.
  const answered =
    event.type === "save-failed" || event.type === "save-abandoned"
      ? { ...state, behindPastSave: false }
      : state;
  return reduce(answered, event);
}
